<template>
  <div>
    <b-breadcrumb :items="breadcrumbItems"></b-breadcrumb>

    <div class="row mb-3">
      <div class="col-sm-4">
        <h4 qid="legal-basis-page-title" class="mb-2">
          {{ $t('maintenance.legal_basis.title') }}
        </h4>
        <div>
          <span v-if="getGuide('legal_basis_list')">
            <GuideButton :guide="getGuide('legal_basis_list')"></GuideButton>
          </span>
        </div>
      </div>
      <div class="col-sm-8 text-right">
        <button
            class="btn btn-secondary-dark float-right"
            @click="filterOpened = !filterOpened"
            qid="filter-lb-button"
        >
          <font-awesome-icon icon="filter"/>
        </button>
        <router-link
            to="/maintenance/legal-basis/create"
            class="btn btn-success float-right"
            qid="create-lb-button"
            v-if="isGranted(permissions.LEGAL_BASIS_CREATE)"
        >
          <font-awesome-icon icon="plus-circle"/> {{ $t('maintenance.legal_basis.create_button') }}
        </router-link>
        <b-form-input
          type="text"
          v-model="filters.keywordValue"
          class="float-right mw-300 mr-3 search-bar"
          qid="search-lb-input"
          v-on:input="debounceInput"
          :placeholder="$t('maintenance.legal_basis.search_placeholder')"
        ></b-form-input>
      </div>
    </div>

    <div class="row" :class="{'table-with-filters': filterOpened}">
      <div class="col-sm-10 table-container">
        <div class="card">
          <table class="table table-responsive-md">
            <thead>
            <tr>
              <th scope="col"
                  class="cursor-pointer"
                  @click="selectSort('name')"
                  qid="sort-lb-name"
              >
                {{ $t('maintenance.legal_basis.table.name') }}
                <span class="ml-2">
                  <font-awesome-icon class="text-muted" icon="sort" v-if="selectedSort !== 'name'"/>
                  <font-awesome-icon icon="caret-down" v-if="selectedDirection === 'desc' && selectedSort === 'name'"/>
                  <font-awesome-icon icon="caret-up" v-if="selectedDirection === 'asc' && selectedSort === 'name'"/>
                </span>
              </th>
              <th scope="col">
                {{ $t('maintenance.legal_basis.table.short_name') }}
              </th>
              <th scope="col"
                  class="cursor-pointer"
                  @click="selectSort('category')"
                  qid="lb-sort-category"
              >
                {{ $t('maintenance.legal_basis.table.category') }}
                <span class="ml-2">
                  <font-awesome-icon class="text-muted" icon="sort" v-if="selectedSort !== 'category'"/>
                  <font-awesome-icon icon="caret-down" v-if="selectedDirection === 'desc' && selectedSort === 'category'"/>
                  <font-awesome-icon icon="caret-up" v-if="selectedDirection === 'asc' && selectedSort === 'category'"/>
                </span>
              </th>
              <th width="130"></th>
            </tr>
            </thead>
            <tbody>
              <tr v-for="object in tableItems">
                <td>
                  <router-link
                      :to="{ name: 'maintenance.legal_basis.view', params: { slug: object.slug }}"
                      qid="lb-view-page-link"
                  >
                    {{object.name}}
                  </router-link>
                </td>
                <td>
                  <span qid="lb-short-name">
                    {{ object.short_name }}
                  </span>
                </td>
                <td>
                  <span qid="lb-category">
                    {{ object.category }}
                  </span>
                </td>
                <td>
                  <span v-if="isGranted([permissions.LEGAL_BASIS_EDIT, permissions.LEGAL_BASIS_EDIT_OWN], object.created_by) && (!getClient.parent || (getClient.parent && object.partition))"
                        class="btn btn-edit"
                        qid="edit-lb-modal"
                        @click="showEditLBModal(object)"
                  >
                    <font-awesome-icon icon="pen"/>
                  </span>
                  <span v-if="isGranted([permissions.LEGAL_BASIS_DELETE, permissions.LEGAL_BASIS_DELETE_OWN], object.created_by) && (!getClient.parent || (getClient.parent && object.partition))"
                        class="btn btn-delete"
                        qid="delete-lb-modal"
                        @click="showConfirmDeleteModal(object)"
                  >
                    <font-awesome-icon icon="trash-alt"/>
                  </span>
                </td>
              </tr>
              <tr v-if="!loading && !tableItems.length" qid="no-results-row" class="text-center">
                <td colspan="10">
                  <h5 class="mt-3">
                    {{ $t('system.no_results') }}
                  </h5>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <div class="mt-3 pagination row">
          <div class="col-md-6">
            <div class="row">
              <div class="float-left">
                <span class="text-muted pagination-counter">
                  {{ $t('pagination.entries_per_page') }}:
                </span>
              </div>
              <div class="float-left">
                <multiselect
                    v-model="perPage"
                    :options="perPageOptions"
                    track-by="value"
                    label="value"
                    :select-label="$t('system.dropdown_select_label')"
                    :selected-label="$t('system.dropdown_selected_label')"
                    :deselect-label="$t('system.dropdown_deselect_label')"
                    qid="create-org-form-organization-level"
                    @select="perPageChanged"
                    :placeholder="selectedPerPage"
                    @remove="perPageRemoved"
                ></multiselect>
              </div>
            </div>
          </div>
          <div class="col-md-6">
            <b-pagination
                class="float-right"
                v-model="currentPage"
                :total-rows="rows"
                :per-page="selectedPerPage"
                aria-controls="my-table"
                @change="pageChanged"
                limit="1"
            >
              <span class="text-muted" slot="first-text"><font-awesome-icon icon="angle-double-left"/></span>
              <span class="text-muted" slot="prev-text"><font-awesome-icon icon="angle-left"/></span>
              <span class="text-muted" slot="next-text"><font-awesome-icon icon="angle-right"/></span>
              <span class="text-muted" slot="last-text"><font-awesome-icon icon="angle-double-right"/></span>
              <div slot="ellipsis-text">
                <b-spinner small type="grow"></b-spinner>
                <b-spinner small type="grow"></b-spinner>
                <b-spinner small type="grow"></b-spinner>
              </div>
              <span class="text-muted" slot="page" slot-scope="{ page, active }">
                <span v-if="active">{{getPerPage}}</span>
              </span>
            </b-pagination>
          </div>
        </div>
      </div>
      <div class="col-sm-2 filter-container">
        <div class="card">
          <div class="card-body">
            <b-form-group>
              <label qid="filter-lb-category-label">
                {{ $t('maintenance.legal_basis.create.category_label') }}
              </label>
              <multiselect
                  v-model="filters.category"
                  :options="categoryItems"
                  select-label="✔"
                  selected-label="✔"
                  deselect-label="x"
                  :placeholder="$t('maintenance.legal_basis.create.category_placeholder')"
                  label="category"
                  @select="debounceInput"
                  @remove="debounceInput"
                  qid="filter-lb-form-category"
              ></multiselect>
            </b-form-group>
            <a @click="resetFilters" class="text-right" qid="lb-filters-reset">
              {{ $t('system.reset_filters') }}
            </a>
          </div>
        </div>
      </div>
    </div>


    <!-- Delete Legal basis confirm modal template -->
    <b-modal ref="delete-legal-basis" hide-footer>
      <div class="d-block text-center">
        <h4 qid="delete-lb-title">
          {{ $t('maintenance.legal_basis.delete.title') }}
        </h4>
      </div>
      <h5 class="text-center">
        {{ $t('maintenance.legal_basis.delete.description') }} {{ itemToDelete.name }}
      </h5>
      <h5 class="text-center">
        {{ $t('maintenance.legal_basis.delete.are_you_sure') }}
      </h5>
      <div class="text-center">
        <b-button class="mt-3"
                  variant="secondary"
                  @click="hideConfirmDeleteModal"
                  qid="delete-lb-cancel-button"
        >
          {{ $t('maintenance.legal_basis.delete.cancel') }}
        </b-button>
        <b-button @click="confirmDeleteLB(itemToDelete)"
                  class="mt-3 ml-3"
                  variant="success"
                  qid="delete-lb-submit-button"
                  v-if="isGranted([permissions.LEGAL_BASIS_DELETE, permissions.LEGAL_BASIS_DELETE_OWN], itemToDelete.created_by) && (!getClient.parent || (getClient.parent && itemToDelete.partition))"
        >
          {{ $t('maintenance.legal_basis.delete.submit') }}
        </b-button>
      </div>
    </b-modal>


    <!-- Edit legal basis modal template -->
    <b-modal ref="edit-legal-basis" hide-footer>
      <div class="d-block text-center">
        <h4 qid="update-lb-title">
          {{ $t('maintenance.legal_basis.update.title') }}
        </h4>
      </div>
      <b-alert v-if="error.length"
               show
               variant="danger"
               qid="edit-lb-error"
      >
        {{ error }}
      </b-alert>
      <b-form @submit="editLBFormSubmit" qid="create-lb-form">
        <div class="form-container">
          <b-form-group>
            <label qid="edit-lb-name-label">
              {{ $t('maintenance.legal_basis.create.name_label') }}
            </label>
            <b-form-input
              qid="edit-lb-form-name"
              type="text"
              v-model="lbForm.name"
              :maxLength="255"
              required
              :placeholder="$t('maintenance.legal_basis.create.name_placeholder')"
            ></b-form-input>
          </b-form-group>
          <b-form-group>
            <label qid="edit-lb-short-name-label">
              {{ $t('maintenance.legal_basis.create.short_name_label') }}
            </label>
            <b-form-input
              qid="edit-lb-form-short-name"
              type="text"
              v-model="lbForm.short_name"
              required
              :placeholder="$t('maintenance.legal_basis.create.short_name_placeholder')"
            ></b-form-input>
          </b-form-group>
          <b-form-group>
            <label qid="edit-lb-description-label">
              {{ $t('maintenance.legal_basis.create.description_label') }}
            </label>
            <b-form-textarea
              qid="edit-lb-form-description"
              v-model="lbForm.description"
              rows="6"
              required
              max-rows="6"
              :placeholder="$t('maintenance.legal_basis.create.description_placeholder')"
            ></b-form-textarea>
          </b-form-group>
          <b-form-group :class="{'multiselect--show-input': categoryInput.length}">
            <label qid="edit-lb-category-label">
              {{ $t('maintenance.legal_basis.create.category_label') }}
            </label>
            <multiselect
                v-model="lbForm.category"
                :options="categoryItems"
                :placeholder="$t('maintenance.legal_basis.create.category_placeholder')"
                :preserveSearch="true"
                :select-label="$t('system.dropdown_select_label')"
                :selected-label="$t('system.dropdown_selected_label')"
                :deselect-label="$t('system.dropdown_deselect_label')"
                @search-change="handleCategoryInput"
                track-by="category"
                label="category"
                qid="edit-lb-form-category"
            >
              <template slot="noResult" slot-scope="props">
                {{ $t('maintenance.legal_basis.create.category_no_results') }}
              </template>
            </multiselect>
          </b-form-group>
        </div>
        <div class="text-right">
          <b-button class="mt-3"
                    variant="secondary"
                    @click="hideEditLBModal"
                    qid="edit-lb-cancel-button"
          >
            {{ $t('maintenance.legal_basis.create.cancel') }}
          </b-button>
          <b-button type="submit"
                    class="mt-3 ml-3"
                    variant="success"
                    :disabled="buttonDisabled"
                    qid="edit-lb-submit-button"
          >
            <span v-if="buttonDisabled" class="mr-1">
              <font-awesome-icon icon="spinner" size="sm" class="fa-spin"/>
            </span>
            {{ $t('maintenance.legal_basis.create.submit') }}
          </b-button>
        </div>
      </b-form>
    </b-modal>

  </div>
</template>

<script>
  import piincHttp from '@/util/configureAxios.js';
  import GuideButton from '@/components/guide/GuideButton.vue';


  export default {
    name: 'LegalBasis',
    data: function() {
      return {
        breadcrumbItems: [
          {
            text: this.$t('dashboard.title'),
            to: '/'
          },
          {
            text: this.$t('maintenance.title'),
            to: '/maintenance'
          },
          {
            text: this.$t('maintenance.legal_basis.title'),
            active: true
          }
        ],
        tableItems: [],

        // Pagination
        perPageOptions: [
          { name: '10', value: '10' },
          { name: '20', value: '20' },
          { name: '50', value: '50' },
          { name: '100', value: '100' }
        ],
        perPage: '',
        selectedPerPage: "10",
        currentPage: 1,
        totalRows: 0,

        itemToDelete: {},
        lbForm: {
          slug: '',
          name: '',
          description: '',
          category: [],
          client:'',
        },
        error: '',
        selectedSort: '',
        selectedDirection: '',
        filterOpened: false,
        keywordValue: '',
        filters: {
          category: [],
          keywordValue: ''
        },
        categoryItems: [],
        categoryInput: '',
        loading: true,
        buttonDisabled: false,
        myGuides: [],
      }
    },
    computed: {
      rows() {
        // Use computed value for rows because of dynamic update
        return this.totalRows
      },
      getClient() {
        return this.$store.state.client
      },
      getFilters() {
        return this.$store.state.activeFilters
      },
      getStorePerPage() {
        return this.$store.state.perPage
      },
      getPerPage() {
        let page = this.currentPage;
        let selectedFrom = (page-1) * Number(this.selectedPerPage) + 1;
        let selectedTo = (page-1) * Number(this.selectedPerPage) + Number(this.selectedPerPage);
        let selectedOf = this.totalRows;

        if(selectedTo > selectedOf){
          selectedTo = selectedOf
        }

        return selectedFrom + " - " + selectedTo + " " + this.$t('system.of') + " " + selectedOf
      }
    },
    watch: {
      // Watch when client changes in store and trigger api reload
      getClient: function () {
        this.loadContent();
        this.loadCategories();
      }
    },
    methods: {
      debounceInput: _.debounce(function() {
        this.loadContent()
      }, 400),
      resetFilters() {
        this.filters.category = ''
        this.debounceInput()
      },
      getGuide(code) {
        let existingGuide = _.find(this.myGuides, function(object) {
          return object.scope == code && object.type == 'Maintenance' && object.enabled
        });
        return existingGuide
      },
      loadGuides(code) {
        let self = this;
        let language = localStorage.getItem('lang')? localStorage.getItem('lang') : 'en';

        piincHttp.get('guides', { params:
          {
            per_page: 1,
            language: 'en',
            type: 'Maintenance',
            scope: code,
            client: this.getClient.slug
          }
        }).then(function(response) {
          if (response.data.items.length) {
            self.myGuides.push(response.data.items[0])
          }
        }, function() {});
      },
      selectSort(sort) {
        // If old and new sort are same, switch just direction
        if (sort === this.selectedSort) {
          this.selectedDirection = this.selectedDirection === 'desc' ? 'asc' : 'desc';
        } else {
          // If different sort, set sort and set direction to default
          this.selectedDirection = 'desc'
          this.selectedSort = sort;
        }
        this.loadContent();
      },
      handleCategoryInput(input) {
        this.categoryInput = input;
      },
      loadContent(page, perPage) {

        if (this.getStorePerPage) {
          this.selectedPerPage = this.getStorePerPage;
        }

        // If page is not defined, set page to default 1
        let pageNumber = page ? page : 1;
        let perPageNumber = perPage ? perPage : this.selectedPerPage;
        let self = this;

        if (
          this.filters.keywordValue !== '' ||
          (this.filters.category && this.filters.category.category)
        ) {
          this.filtersOpened = true
        }

        piincHttp.get('articles', { params:
          {
            page: pageNumber,
            per_page: perPageNumber,
            sort: this.selectedSort !== '' ? this.selectedSort : undefined,
            order: this.selectedDirection !== '' ? this.selectedDirection : undefined,
            category: this.filters.category && this.filters.category !== '' ? this.filters.category.category : undefined,
            keyword: this.filters.keywordValue !== '' ? this.filters.keywordValue : undefined,
            client: this.getClient.slug
          }
        }).then(function(response) {
          self.loading = false
          self.tableItems = response.data.items;
          // Set number of total items
          self.totalRows = response.data.total_count;
        }, function() {});
      },
      pageChanged(page) {
        this.loadContent(page)
      },
      perPageChanged(perPage) {
        let self = this;
        // Load content when page changed listener is triggered
        this.loadContent(1, perPage.value)
        self.selectedPerPage = perPage.value
        this.$store.commit('storePerPage', this.selectedPerPage);
      },
      perPageRemoved() {
        let self = this;
        // Load content when page changed listener is triggered
        self.selectedPerPage = self.perPageOptions[0].value
        this.loadContent(1, self.selectedPerPage)
      },
      showConfirmDeleteModal(object) {
        this.itemToDelete = object
        this.$refs['delete-legal-basis'].show()
      },
      hideConfirmDeleteModal() {
        this.itemToDelete = {}
        this.$refs['delete-legal-basis'].hide()
      },
      showEditLBModal(object) {

        let self = this;

        // Preselect category on edit modal
        this.lbForm.category = []
        if (object.category) {
          _.find(this.categoryItems, function(item) {
            if (item.category == object.category) {
              self.lbForm.category = item;
            }
          });
        }

        this.lbForm.slug = object.slug;
        this.lbForm.name = object.name;
        this.lbForm.short_name = object.short_name;
        this.lbForm.description = object.description;
        this.$refs['edit-legal-basis'].show()
      },
      hideEditLBModal() {
        this.categoryInput = ''
        this.$refs['edit-legal-basis'].hide()
      },
      editLBFormSubmit(evt) {
        this.buttonDisabled = true;
        // Keep the reference in a variable
        // to use it inside function called when promise returned
        let self = this;
        evt.preventDefault();
        this.lbForm.client = this.getClient.slug;

        if (this.categoryInput && this.categoryInput.length) {
          this.lbForm.category = this.categoryInput
        } else {
          if (this.lbForm.category && this.lbForm.category.category) {
            this.lbForm.category = this.lbForm.category.category
          } else {
            this.lbForm.category = '';
          }
        }

        piincHttp.put('articles/' + this.lbForm.slug, this.lbForm).then(function() {
          self.hideEditLBModal();
          self.loadContent(self.currentPage);
          self.loadCategories();
          self.buttonDisabled = false;
          self.$toastr('success', self.$t('maintenance.legal_basis.update.success'))
        }, function(error) {
          if (error.status !== 200) {
            self.buttonDisabled = false;
            self.error = error.data.message;
            if (error.data.errors.category) {
              self.error += ' ' + error.data.errors.category[0]
            }
          }
        })
      },
      confirmDeleteLB(object) {
        let self = this;
        piincHttp.delete('articles/' + object.slug).then(function(response) {
          self.hideConfirmDeleteModal();
          self.loadContent(self.currentPage);
          // Trigger toastr message after modal close
          self.$toastr('success', self.$t('maintenance.legal_basis.delete.success'))
        }, function() {});
      },
      loadCategories() {
        let self = this;

        piincHttp.get('/articles/categories', { params:
          {
            client: this.getClient.slug
          }
        }).then(function(response) {
          self.categoryItems = response.data
          if (self.categoryItems && !self.categoryItems.length) {
            self.categoryItems.push({category: self.$t('system.no_results'), $isDisabled: true})
          }
        }, function() {});
      }
    },
    components: {
      GuideButton
    },
    beforeRouteLeave(to, from, next) {
      let object = {
        type: 'legal_basis',
        filters: this.filters
      }
      this.$store.commit('storeActiveFilters', object);
      this.$store.commit('storePerPage', this.selectedPerPage);
      next()
    },
    created () {
      if (this.getClient && this.getClient.slug) {
        if (this.getFilters.legal_basis) {
          this.filters = this.getFilters.legal_basis
        }
        this.loadContent();
        this.loadCategories();
      }
      this.loadGuides('legal_basis_list');
    }
  }
</script>
